<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  /**
   * @type {{data: Record<string, any>[], cols?: string[], rowNumbers?: boolean, labelColumn?: boolean, formatters?: Record<string, (value: any) => string>, headers?: Record<string, string>, sortBy?: string, sortDirection?: "ascending" | "descending", collapsible?: boolean, maxHeight?: string, scrollable?: boolean}}
   */
  import IconChevronDown from "$lib/components/Icons/IconChevronDown.svelte";

  const {
    data,
    cols,
    rowNumbers,
    labelColumn = true,
    formatters = {},
    headers = {},
    sortBy,
    sortDirection = "ascending",
    collapsible = false,
    maxHeight = "15em",
    scrollable = false
  } = $props();

  let expanded = $state(false);

  // Reset expanded when data changes
  $effect(() => {
    data;
    expanded = false;
  });

  let colNames = $derived(cols ? cols : data.length > 0 ? Object.keys(data[0]) : []);

  /** @type {("number" | "text")[]} */
  let colTypes = $derived(
    colNames.length > 0 && data.length > 0
      ? colNames.map((col) => {
          let checkRow = 0;
          // check if columns is number
          let checkVal = data[checkRow][col];
          // if value is NA, check next column
          while ((checkVal === "NA" || checkVal === "N/A") && checkRow < data.length - 1) {
            checkRow++;
            checkVal = data[checkRow][col];
          }
          if (typeof checkVal === "number") {
            return "number";
          } else if (checkRow === data.length - 1 && (checkVal === "NA" || checkVal === "N/A")) {
            // if we make it all the way to the end and the value is still NA, still treat it like a number
            return "number";
          }
          return "text";
        })
      : []
  );

  let sortedData = $derived(
    sortBy && data.length > 0
      ? [...data].sort((a, b) => {
          const colIndex = colNames.indexOf(sortBy);
          const isNumeric = colIndex >= 0 && colTypes[colIndex] === "number";
          const dir = sortDirection === "descending" ? -1 : 1;
          if (isNumeric) {
            return dir * ((a[sortBy] ?? 0) - (b[sortBy] ?? 0));
          }
          return dir * String(a[sortBy] ?? "").localeCompare(String(b[sortBy] ?? ""));
        })
      : data
  );

  let isCollapsed = $derived(collapsible && !expanded);
  let showToggle = $derived(collapsible);

  let wrapperStyle = $derived(
    isCollapsed ? `max-height: ${maxHeight}; overflow-y: ${scrollable ? "auto" : "hidden"};` : ""
  );
</script>

<div class="table-outer" class:label-column={labelColumn}>
  <div class="table-wrapper" class:scrollable={scrollable && isCollapsed} style={wrapperStyle}>
    <table class="tool-table">
      <thead>
        <tr class="row header">
          {#if rowNumbers}
            <th class="cell row-number" scope="col"></th>
          {/if}
          {#each colNames as col, colIndex}
            {@const colType = colTypes[colIndex]}
            <th
              class="cell"
              class:type-text={colType === "text"}
              class:type-number={colType === "number"}
              scope="col"
              data-field={col}>{@html headers[col] || col}</th
            >
          {/each}
        </tr>
      </thead>
      <tbody>
        {#each sortedData as row, rowIndex}
          <tr class="row">
            {#each colNames as col, colIndex}
              {@const colType = colTypes[colIndex]}
              {@const formatter =
                colType === "number" && formatters[col]
                  ? formatters[col]
                  : (/** @type {any} */ val) => val}
              {#if rowNumbers && colIndex === 0}
                <td class="cell row-number" data-field="row-number">{rowIndex}</td>
              {/if}
              {#if colIndex === 0}
                <th
                  class="cell first"
                  class:type-text={colType === "text"}
                  class:type-number={colType === "number"}
                  scope="row"
                  data-field={col}>{formatter(row[col])}</th
                >
              {:else}
                <td
                  class="cell"
                  class:type-text={colType === "text"}
                  class:type-number={colType === "number"}
                  data-field={col}>{formatter(row[col])}</td
                >
              {/if}
            {/each}
          </tr>
        {/each}
      </tbody>
    </table>
    {#if isCollapsed}
      <div class="table-spacer"></div>
      <div class="table-fade"></div>
    {/if}
  </div>
  {#if showToggle}
    <button class="table-toggle" onclick={() => (expanded = !expanded)}>
      <span class="toggle-rule"></span>
      <span class="toggle-label">{expanded ? "Collapse" : "Expand"}</span>
      <span class="toggle-rule"></span>
      <span class="toggle-arrow" class:expanded
        ><IconChevronDown size={18} fill="currentColor" /></span
      >
    </button>
  {/if}
</div>

<style>
  .table-outer {
    --table-spacer-size: 20px;
    width: 100%;
  }
  .table-wrapper {
    position: relative;
    overflow-x: auto;
    scrollbar-color: var(--color-gray) var(--color-white);
  }
  .table-wrapper::-webkit-scrollbar-thumb {
    /* Foreground */
    background: var(--color-white);
  }
  .table-wrapper::-webkit-scrollbar-track {
    /* Background */
    background: var(--color-gray-shade-lighter);
  }
  .table-wrapper table.tool-table {
    margin: 0 auto;
    min-width: 100%;
    border-spacing: 0;
    font-size: var(--font-size-small);
  }

  /*** TABLE HEADER ***/
  .table-outer .row.header .cell {
    border-bottom: 2px solid var(--color-black);
    padding-bottom: var(--spacing-3);
    vertical-align: bottom;
    font-weight: 900;
  }

  /*** TABLE BODY ***/
  .cell {
    padding: var(--spacing-1);
    border-bottom: 2px solid var(--color-gray);
  }
  td.cell {
    color: var(--color-gray-shade-darkest);
    min-width: 8em;
  }
  .cell.type-text {
    text-align: left;
  }
  .cell.type-number {
    text-align: right;
  }
  .table-outer.label-column .cell.first {
    min-width: 8rem;
  }
  .cell.row-number {
    text-align: left;
    min-width: 1em;
  }

  /*** SCROLLABLE STICKY HEADER ***/
  .scrollable thead {
    position: sticky;
    top: 0;
    z-index: 1;
    background: var(--color-gray-shade-lightest);
  }
  .table-spacer {
    height: var(--table-spacer-size);
  }
  /*** FADE OVERLAY ***/
  .table-fade {
    position: sticky;
    bottom: 0;
    height: var(--table-spacer-size);
    margin-top: calc(0 - var(--table-spacer-size));
    pointer-events: none;
    background: linear-gradient(to bottom, transparent, var(--color-gray-shade-lightest));
  }
  .table-toggle {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    grid-template-rows: auto auto;
    align-items: center;
    column-gap: var(--spacing-4);
    row-gap: var(--spacing-2);
    width: 100%;
    padding: 0;
    background: none;
    border: none;
    cursor: pointer;
  }
  .toggle-rule {
    display: block;
    opacity: 0;
    height: 1px;
    background: var(--color-gray);
  }
  .toggle-label {
    font-family: "Lato", sans-serif;
    font-style: italic;
    font-size: var(--font-size-normal);
    color: var(--color-gray-shade-darkest);
    text-decoration: underline;
  }
  .toggle-arrow {
    grid-column: 1 / -1;
    display: flex;
    justify-content: center;
    color: var(--color-gray-shade-darkest);
    transition: transform 0.2s ease;
  }
  .toggle-arrow.expanded {
    transform: rotate(180deg);
  }
</style>
